import { isQueryErrorResult } from '@src/client';
import {
  AssistantApiBody,
  AssistantApiRecord,
  AssistantForm,
  AssistantMessageDefaults,
  AssistantMessageDefaultsResponse,
  UnsupportedContentType,
  UnsupportedFileMessages,
  UnsupportedMessageApiField,
  UnsupportedMessagesDefaultsFlags,
} from '@src/types/assistant';

/** Content type → field name on the assistant API (F001 `03-api-design.md`). */
export const UNSUPPORTED_MESSAGE_API_FIELD: Record<
  UnsupportedContentType,
  UnsupportedMessageApiField
> = {
  [UnsupportedContentType.DOCUMENTS]: 'unsupportedDocumentMessage',
  [UnsupportedContentType.VIDEOS]: 'unsupportedVideoMessage',
  [UnsupportedContentType.OTHERS]: 'unsupportedOtherContentMessage',
};

/** Content type → key of the read-only `unsupportedMessagesDefaults` object. */
export const UNSUPPORTED_MESSAGE_FLAG_KEY: Record<
  UnsupportedContentType,
  keyof UnsupportedMessagesDefaultsFlags
> = {
  [UnsupportedContentType.DOCUMENTS]: 'document',
  [UnsupportedContentType.VIDEOS]: 'video',
  [UnsupportedContentType.OTHERS]: 'otherContent',
};

const CONTENT_TYPES = Object.values(UnsupportedContentType);

/**
 * API record → form. The three effective texts and their flags collapse into
 * `unsupportedFileMessages`: a field flagged as default (or missing/blank, as
 * an older backend would send — RN02) becomes `undefined`; the agent's own
 * text is kept as is. The flat fields never reach the form.
 */
export const fromAssistantApi = (record: AssistantApiRecord): AssistantForm => {
  const {
    unsupportedDocumentMessage,
    unsupportedVideoMessage,
    unsupportedOtherContentMessage,
    unsupportedMessagesDefaults,
    ...rest
  } = record;
  const texts = {
    unsupportedDocumentMessage,
    unsupportedVideoMessage,
    unsupportedOtherContentMessage,
  };

  const unsupportedFileMessages = CONTENT_TYPES.reduce<UnsupportedFileMessages>(
    (acc, type) => {
      const text = texts[UNSUPPORTED_MESSAGE_API_FIELD[type]];
      const isDefault =
        unsupportedMessagesDefaults?.[UNSUPPORTED_MESSAGE_FLAG_KEY[type]] ===
        true;
      return {
        ...acc,
        [type]: isDefault || !text?.trim() ? undefined : text,
      };
    },
    {}
  );

  return { ...rest, unsupportedFileMessages };
};

/**
 * Form → API body. Each content type travels as its own flat field; a missing
 * value (system default, or restored) is sent as an explicit `null` so the
 * payload is auditable. The form-only keys never reach the wire.
 */
export const toAssistantApiBody = (form: AssistantForm): AssistantApiBody => {
  const { unsupportedFileMessages, ...rest } = form;
  delete (rest as Partial<AssistantForm>).followUp;
  return CONTENT_TYPES.reduce<AssistantApiBody>(
    (acc, type) => ({
      ...acc,
      [UNSUPPORTED_MESSAGE_API_FIELD[type]]:
        unsupportedFileMessages?.[type] ?? null,
    }),
    rest as AssistantApiBody
  );
};

/** `GET /assistants/message-defaults` → defaults keyed by content type. */
export const messageDefaultsFromApi = (
  response: AssistantMessageDefaultsResponse
): AssistantMessageDefaults =>
  CONTENT_TYPES.reduce(
    (acc, type) => ({
      ...acc,
      [type]: response[UNSUPPORTED_MESSAGE_API_FIELD[type]],
    }),
    {} as AssistantMessageDefaults
  );

const API_FIELD_PATTERN = /unsupported(Document|Video|OtherContent)Message/;

/**
 * Which content type a `400` from the assistant API is about, read from the
 * field name quoted in its `message` ("The 'unsupportedVideoMessage' field
 * cannot be blank and must be a maximum of 1000 characters."). The wording
 * varies between the design and the service, so only the name is matched.
 * `null` for any other error.
 */
export const parseUnsupportedMessageFieldError = (
  error: unknown
): UnsupportedContentType | null => {
  if (!isQueryErrorResult(error) || error.status !== 400) return null;
  const message = (error.data as { message?: unknown } | undefined)?.message;
  if (typeof message !== 'string') return null;
  const field = message.match(API_FIELD_PATTERN)?.[0];
  return (
    CONTENT_TYPES.find(type => UNSUPPORTED_MESSAGE_API_FIELD[type] === field) ??
    null
  );
};
